import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { getEntitySignals, getPersonNetwork } from '../../../services/apiClient'
import type { EntitySignals, PersonNetwork } from '../../../types/network'
import WorthKnowing from './WorthKnowing'
import NetworkList, { type NetworkTime } from './NetworkList'
import NetworkMap from './NetworkMap'
import { findings } from './networkText'

type View = 'list' | 'map'

const SEGMENT =
  'inline-flex rounded-full border border-rule-strong bg-paper p-0.5'
const segmentButton = (on: boolean) =>
  `h-7 rounded-full px-3 text-[13px] font-semibold transition-colors ${
    on ? 'bg-ink text-paper' : 'text-ink-600 hover:text-ink'
  }`

/**
 * »Forbindelser« on a person: what is worth knowing, then the network as a
 * list or a map, now or across all years. Fetched the first time the tab
 * is opened (`enabled`), then kept.
 */
const PersonNetworkTab: React.FC<{
  enhedsNummer: string
  personName: string
  enabled: boolean
}> = ({ enhedsNummer, personName, enabled }) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const view: View = searchParams.get('visning') === 'kort' ? 'map' : 'list'
  const time: NetworkTime = searchParams.get('tid') === 'alle' ? 'all' : 'now'

  const [network, setNetwork] = useState<PersonNetwork | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [signals, setSignals] = useState<EntitySignals | null>(null)
  const [signalsDone, setSignalsDone] = useState(false)
  // Which person the data was requested for; answers for an older request
  // (the user moved on to another person) are dropped. Switching tabs must
  // not cancel a request, so there is no effect cleanup.
  const requestedFor = useRef<string | null>(null)
  const started = requestedFor.current === enhedsNummer

  useEffect(() => {
    if (!enabled || requestedFor.current === enhedsNummer) return
    requestedFor.current = enhedsNummer
    const mine = () => requestedFor.current === enhedsNummer
    setNetwork(null)
    setError(null)
    setSignals(null)
    setSignalsDone(false)
    getPersonNetwork(enhedsNummer)
      .then(n => mine() && setNetwork(n))
      .catch((e: Error) => mine() && setError(e.message))
    getEntitySignals('person', enhedsNummer)
      .then(s => mine() && setSignals(s))
      .catch(() => undefined)
      .finally(() => mine() && setSignalsDone(true))
  }, [enabled, enhedsNummer])

  const items = useMemo(
    () => findings(signals, network?.patterns ?? []),
    [signals, network]
  )

  const setParam = (key: string, value: string | null) =>
    setSearchParams(
      prev => {
        const p = new URLSearchParams(prev)
        if (value === null) p.delete(key)
        else p.set(key, value)
        return p
      },
      { replace: true }
    )

  const currentCount = network?.connections.filter(c => c.current).length ?? 0

  return (
    <div className="space-y-5">
      <WorthKnowing items={items} loadingSignals={!signalsDone && started} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className={SEGMENT} role="group" aria-label="Visning">
          <button
            type="button"
            aria-pressed={view === 'list'}
            className={segmentButton(view === 'list')}
            onClick={() => setParam('visning', null)}
          >
            Liste
          </button>
          <button
            type="button"
            aria-pressed={view === 'map'}
            className={segmentButton(view === 'map')}
            onClick={() => setParam('visning', 'kort')}
          >
            Kort
          </button>
        </div>
        <div className={SEGMENT} role="group" aria-label="Periode">
          <button
            type="button"
            aria-pressed={time === 'now'}
            className={segmentButton(time === 'now')}
            onClick={() => setParam('tid', null)}
          >
            Nu
            {network ? (
              <span className="ml-1 tabular-nums opacity-70">
                {currentCount}
              </span>
            ) : null}
          </button>
          <button
            type="button"
            aria-pressed={time === 'all'}
            className={segmentButton(time === 'all')}
            onClick={() => setParam('tid', 'alle')}
          >
            Alle år
            {network ? (
              <span className="ml-1 tabular-nums opacity-70">
                {network.connections.length}
              </span>
            ) : null}
          </button>
        </div>
      </div>

      {error ? (
        <p className="rounded-sm border border-signal-200 bg-signal-50 px-3 py-2 text-sm text-signal-900">
          {error}
        </p>
      ) : !network ? (
        <p className="text-[13px] font-semibold text-ink-500">
          Gennemgår {personName ? `${personName}s` : 'personens'} selskaber…
        </p>
      ) : (
        <>
          {view === 'map' ? (
            <NetworkMap network={network} time={time} />
          ) : (
            <NetworkList connections={network.connections} time={time} />
          )}
          <p className="text-[12px] text-ink-400">
            Ud fra{' '}
            {network.truncated
              ? `de ${network.companiesChecked} nyeste af `
              : ''}
            {network.companiesTotal}{' '}
            {network.companiesTotal === 1 ? 'selskab' : 'selskaber'}, personen
            har eller har haft rolle i. Medejere, der er selskaber, er fulgt til
            personerne bag. Revisorer tæller ikke med.{' '}
            <Link
              to={`/forbindelse?fra=person:${enhedsNummer}&fraNavn=${encodeURIComponent(personName)}`}
              className="font-semibold text-ink-600 hover:text-ink hover:underline decoration-ink-300 underline-offset-2"
            >
              Find vej til en bestemt person eller et selskab
            </Link>
          </p>
        </>
      )}
    </div>
  )
}

export default PersonNetworkTab
